'use client'
import React, { forwardRef, useEffect, useState } from 'react'
import { CaretUpDownIcon } from '@phosphor-icons/react/dist/ssr/CaretUpDown'
import { CheckCircleIcon } from '@phosphor-icons/react/dist/ssr/CheckCircle'
import { FunnelSimpleIcon } from '@phosphor-icons/react/dist/ssr/FunnelSimple'
import { cx } from 'class-variance-authority'
import { Badge, Button } from '@skop-app/lib-front'
import type { Value } from '@skop-app/lib-domain/types'
import { deduplicate } from '@skop-app/lib-domain/utils'

import { Separator } from '@/components/atoms/Separator'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/atoms/Command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/atoms/Popover'

import {
  ButtonSelectProps,
  ButtonWithBadgesProps,
  CheckboxListProps,
  ComboboxProps,
  ComboboxTypes,
  GroupListProps,
  ListProps,
} from './combobox.type'

const List = ({
  empty,
  options,
  values,
  setValues,
  setOpen,
  customActions = [],
  multiple,
}: ListProps) => {
  const handleState = (item: Value) => {
    const newValues = values[0] === item ? [] : [item]
    setValues(newValues)
    setOpen(false)
  }
  const handleArrayState = (item: Value) => {
    const newValues = values.includes(item)
      ? values.filter((v) => v !== item)
      : [...values, item]
    setValues(newValues)
  }

  return (
    <>
      <CommandGroup>
        <CommandEmpty>{empty}</CommandEmpty>
        {options.map((option) => (
          <CommandItem
            key={option.value}
            value={option.label}
            onSelect={() =>
              multiple
                ? handleArrayState(option.value)
                : handleState(option.value)
            }
            className="flex justify-between"
          >
            <span>{option.label}</span>
            <CheckCircleIcon
              className={cx(
                'ml-2 size-4 shrink-0',
                values.includes(option.value) ? 'opacity-100' : 'opacity-0',
              )}
            />
          </CommandItem>
        ))}
      </CommandGroup>
      {customActions.length > 0 && (
        <CommandGroup className="mt-2 border-t pt-2">
          {customActions?.map(({ label, action }) => {
            return (
              <CommandItem
                key={label?.toString()}
                onSelect={() => {
                  action()
                  setOpen(false)
                }}
                className="mt-1 flex justify-between"
              >
                <span>{label}</span>
              </CommandItem>
            )
          })}
        </CommandGroup>
      )}
    </>
  )
}

const GroupList = ({
  groupOptions,
  values,
  setValues,
  setOpen,
  multiple,
}: GroupListProps) => {
  const handleState = (item: Value) => {
    const newValues = values[0] === item ? [] : [item]
    setValues(newValues)
    setOpen(false)
  }
  const handleArrayState = (item: Value) => {
    const newValues = values.includes(item)
      ? values.filter((v) => v !== item)
      : [...values, item]
    setValues(newValues)
  }

  return (
    <>
      {groupOptions.map((group) => (
        <CommandGroup heading={group.label} key={group.value}>
          {group.options?.map((item) => (
            <CommandItem
              key={item.value}
              value={item.label}
              onSelect={() =>
                multiple
                  ? handleArrayState(item.value)
                  : handleState(item.value)
              }
              className="flex justify-between"
            >
              <span>{item.label}</span>
              <CheckCircleIcon
                className={cx(
                  'size-4 shrink-0',
                  values.includes(item.value) ? 'opacity-100' : 'opacity-0',
                )}
              />
            </CommandItem>
          ))}
        </CommandGroup>
      ))}
    </>
  )
}

const CheckboxList = ({ options, values, setValues }: ListProps) => {
  const toggleValue = (value: Value) => {
    setValues((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    )
  }

  return (
    <CommandGroup>
      {options.map((option) => {
        const id = `combobox-option-${String(option.value)}`
        return (
          <CommandItem
            key={option.value}
            value={option.label}
            onSelect={() => toggleValue(option.value)}
            className="cursor-pointer"
          >
            <input
              id={id}
              type="checkbox"
              checked={values.includes(option.value)}
              onChange={() => toggleValue(option.value)}
              onClick={(e) => e.stopPropagation()}
              className="mr-2 size-4 rounded border-slate-100 text-orange-500 focus:ring-orange-500 "
            />
            <label
              className="w-full cursor-pointer"
              htmlFor={id}
              onClick={(e) => e.stopPropagation()}
            >
              {option.label}
            </label>
          </CommandItem>
        )
      })}
    </CommandGroup>
  )
}

const GroupCheckboxList = ({
  groupOptions = [],
  values,
  setValues,
}: CheckboxListProps) => {
  const toggleValue = (value: Value) => {
    setValues((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    )
  }

  return (
    <>
      {groupOptions.map((group) => (
        <CommandGroup heading={group.label} key={group.value}>
          {group.options?.map((item) => {
            const id = `combobox-option-${String(group.value)}-${String(
              item.value,
            )}`
            return (
              <CommandItem
                key={item.value}
                value={item.label}
                onSelect={() => toggleValue(item.value)}
                className="cursor-pointer"
              >
                <input
                  id={id}
                  type="checkbox"
                  checked={values.includes(item.value)}
                  onChange={() => toggleValue(item.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="mr-2 size-4 rounded border-slate-100 text-orange-500 focus:ring-orange-500 "
                />
                <label
                  className="w-full cursor-pointer"
                  htmlFor={id}
                  onClick={(e) => e.stopPropagation()}
                >
                  {item.label}
                </label>
              </CommandItem>
            )
          })}
        </CommandGroup>
      ))}
    </>
  )
}

const ContentComponent = {
  list: List,
  group: GroupList,
  checkbox: CheckboxList,
}
export * from './combobox.type'

export function Combobox({
  values,
  setValues,
  label,
  placeholder = 'Rechercher',
  empty = 'Aucun résultat',
  triggerType = 'select',
  listType = 'list' as ComboboxTypes,
  multiple = false,
  options: defaultOptions = [],
  groupOptions = [],
  style,
  defaultOpened = false,
  customActions = [],
  disabled = false,
}: ComboboxProps) {
  const [open, setOpen] = useState(defaultOpened)
  const isOpen = open && !disabled
  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])
  let options = defaultOptions.length
    ? defaultOptions
    : groupOptions.flatMap((group) => group.options ?? [])
  options = deduplicate(options, 'value')

  const Component =
    listType === 'checkbox' && groupOptions.length > 0
      ? GroupCheckboxList
      : ContentComponent[listType]
  const props = {
    options,
    groupOptions,
    values,
    setValues,
    setOpen,
    customActions,
    multiple,
  }

  return (
    <Popover open={isOpen} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild disabled={disabled} className="cursor-pointer">
        {triggerType === 'select' ? (
          <ButtonSelect
            open={isOpen}
            label={label}
            values={values}
            options={options}
            style={style}
          />
        ) : (
          <ButtonWithBadges
            open={isOpen}
            label={label}
            values={values}
            options={options}
            style={style}
          />
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-[220px] bg-white p-0"
        align="start"
        style={style}
      >
        <Command>
          {options.length > 5 && <CommandInput placeholder={placeholder} />}
          <CommandEmpty>{empty}</CommandEmpty>
          <CommandList>
            <Component {...props} />
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

const ButtonSelect = forwardRef<HTMLButtonElement, ButtonSelectProps>(
  ({ open, label, values, options, ...props }, ref) => {
    const badgeCount = values?.length > 1 ? `(+${values.length - 1})` : ''
    const matchedLabel =
      options?.find((option) => option.value === values[0])?.label ||
      values?.[0]
    return (
      <Button
        ref={ref}
        variant="ghost"
        aria-expanded={open}
        className="justify-between"
        {...props}
      >
        {values?.length ? `${matchedLabel} ${badgeCount}` : label}
        <CaretUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
      </Button>
    )
  },
)
ButtonSelect.displayName = 'ButtonSelect'

const ButtonWithBadges = forwardRef<HTMLButtonElement, ButtonWithBadgesProps>(
  ({ open, label, values, options, ...props }, ref) => {
    let limit = 0
    if (typeof window !== 'undefined') {
      limit = window.innerWidth > 640 ? 1 : 0
    }
    return (
      <Button
        ref={ref}
        variant="action"
        aria-expanded={open}
        {...props}
        asChild
      >
        <span>
          <FunnelSimpleIcon className="size-4 shrink-0 opacity-50" />
          <span
            className={cx({
              'pr-2 font-normal lg:pr-0': true,
              'hidden 2xl:block': values?.length,
            })}
          >
            {label}
          </span>
          {values?.length ? (
            <div className="flex">
              <Separator
                orientation="vertical"
                className="mx-2 hidden !h-5 2xl:block"
              />
              {values.slice(0, limit).map((value) => (
                <Badge
                  key={value}
                  variant="dark"
                  className="ml-2 whitespace-nowrap"
                  onClick={() => {}}
                >
                  {options.find((option) => option.value === value)?.label}
                </Badge>
              ))}
              {values.length > limit ? (
                <Badge variant="dark" className="ml-2">
                  {`+${values.length - limit}`}
                </Badge>
              ) : null}
            </div>
          ) : null}
        </span>
      </Button>
    )
  },
)
ButtonWithBadges.displayName = 'ButtonWithBadges'
